import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useFavorites } from '@/hooks/useFavorites';

const navItems = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/movie' },
  { label: '剧集', to: '/category/tv' },
  { label: '综艺', to: '/category/show' },
  { label: '影视资讯', to: '/news' },
  { label: '关于我们', to: '/about' },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { favorites } = useFavorites();
  const inputRef = useRef<HTMLInputElement>(null);

  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const keyword = query.trim();
    if (!keyword) {
      inputRef.current?.focus();
      return;
    }
    navigate(`/search?q=${encodeURIComponent(keyword)}`);
    setMobileOpen(false);
  };

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  const solid = scrolled || !isHome || mobileOpen;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        solid
          ? 'border-b border-background-200/70 bg-background-50/95 backdrop-blur-md'
          : 'border-b border-white/10 bg-transparent'
      }`}
    >
      <div
        className={`hidden w-full items-center justify-between border-b px-4 py-1.5 font-label text-[11px] md:flex md:px-8 ${
          solid ? 'border-background-200/70 text-foreground-500' : 'border-white/10 text-white/60'
        }`}
      >
        <p className="flex items-center gap-1.5">
          <i className="ri-broadcast-line"></i>
          今日更新 24 部 · 新片上线 6 部 · 资讯 3 篇
        </p>
        <p className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <i className="ri-database-2-line"></i>
            内容只为索引与介绍
          </span>
          <Link to="/favorites" className="transition-colors hover:text-accent-600">
            我的片单
          </Link>
        </p>
      </div>
      <div className="flex h-16 w-full items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-foreground-950">
            <i className="ri-play-fill text-lg"></i>
          </span>
          <span className="flex flex-col leading-none">
            <h1 className={`font-display text-lg font-bold tracking-tight ${solid ? 'text-foreground-950' : 'text-white'}`}>
              91大神
            </h1>
            <span className={`mt-0.5 font-display text-[10px] uppercase tracking-[0.22em] ${solid ? 'text-foreground-500' : 'text-white/70'}`}>
              Film &amp; TV
            </span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex" aria-label="主导航">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive(item.to)
                  ? solid
                    ? 'bg-primary-100 text-primary-800'
                    : 'bg-white/15 text-white'
                  : solid
                    ? 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 md:gap-3">
          <form onSubmit={submit} className="hidden md:block">
            <div
              className={`flex h-10 items-center gap-2 rounded-full border pl-4 pr-1.5 transition-colors ${
                solid ? 'border-background-300/70 bg-background-100' : 'border-white/25 bg-white/10'
              }`}
            >
              <i className={`ri-search-line text-sm ${solid ? 'text-foreground-500' : 'text-white/70'}`}></i>
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索影片、剧集、资讯"
                aria-label="站内搜索"
                className={`w-40 bg-transparent text-sm outline-none lg:w-52 ${
                  solid ? 'text-foreground-900 placeholder:text-foreground-400' : 'text-white placeholder:text-white/60'
                }`}
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-accent-500 px-3.5 py-1.5 font-label text-xs font-bold text-foreground-950 transition-colors hover:bg-accent-600"
              >
                搜索
              </button>
            </div>
          </form>

          <Link
            to="/favorites"
            aria-label="我的收藏"
            className={`relative flex h-10 w-10 items-center justify-center rounded-full transition-colors ${
              solid ? 'bg-background-100 text-foreground-700 hover:text-accent-600' : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            <i className="ri-heart-3-line text-base"></i>
            {favorites.length > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 font-label text-[10px] font-bold text-foreground-950">
                {favorites.length}
              </span>
            )}
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="打开菜单"
            aria-expanded={mobileOpen}
            className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors lg:hidden ${
              solid ? 'bg-background-100 text-foreground-800' : 'bg-white/10 text-white'
            }`}
          >
            <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="animate-fade-in border-t border-background-200/70 bg-background-50 px-4 pb-6 pt-4 lg:hidden">
          <form onSubmit={submit} className="mb-4">
            <div className="flex h-11 items-center gap-2 rounded-full border border-background-300/70 bg-background-100 pl-4 pr-1.5">
              <i className="ri-search-line text-sm text-foreground-500"></i>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索影片、剧集、资讯"
                aria-label="站内搜索"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-accent-500 px-3.5 py-1.5 font-label text-xs font-bold text-foreground-950"
              >
                搜索
              </button>
            </div>
          </form>
          <nav className="grid grid-cols-2 gap-2" aria-label="移动端导航">
            {[...navItems, { label: '我的收藏', to: '/favorites' }].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center justify-between rounded-lg border px-3.5 py-3 text-sm font-medium ${
                  isActive(item.to)
                    ? 'border-primary-200 bg-primary-100 text-primary-800'
                    : 'border-background-200/70 bg-background-50 text-foreground-800'
                }`}
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}